import s from "./WorkOrders.module.css";
import type { Tally, WorkOrderStats as Stats } from "../../domain/workOrderReport";
import { locale, t } from "../../i18n";

/** The filter a breakdown row applies when clicked, as a URL parameter. */
export type Dimension =
  "status" | "priority" | "kind" | "repair" | "discipline" | "sla" | "area" | "trade" | "contractor";

const money = (n: number) => `SAR ${n.toLocaleString(locale() ?? "en-US", { maximumFractionDigits: 0 })}`;
const one = (n: number) => n.toLocaleString(locale() ?? "en-US", { maximumFractionDigits: 1 });

/**
 * Statistics for exactly the orders the filters leave.
 *
 * Every derived figure says what it was computed from — "3 of 18 record a
 * cost" — because a cost total over the few orders that carry one reads, on
 * its own, as the cost of all of them.
 */
export function WorkOrderStats({
  stats,
  active,
  onPick,
  interactive = true,
}: {
  stats: Stats;
  /** The current value of each dimension's filter, to mark the row applied. */
  active?: Partial<Record<Dimension, string | null>>;
  onPick?: (dimension: Dimension, key: string) => void;
  /** Off in the printed report, where a row cannot be clicked. */
  interactive?: boolean;
}) {
  const tiles: { label: string; value: string; note?: string; tone?: "bad" }[] = [
    { label: t("Orders"), value: String(stats.total) },
    { label: t("Open"), value: String(stats.open) },
    { label: t("Overdue"), value: String(stats.overdue), tone: stats.overdue ? "bad" : undefined },
    { label: t("P1 open"), value: String(stats.p1Open), tone: stats.p1Open ? "bad" : undefined },
    {
      label: t("Completed"),
      value: String(stats.closed),
      note: stats.cancelled ? t("{n} canceled", { n: stats.cancelled }) : undefined,
    },
    {
      label: t("Mean days to complete"),
      value: stats.meanDaysToComplete == null ? "—" : one(stats.meanDaysToComplete),
      note: stats.completedWithDates ? t("over {n} completed", { n: stats.completedWithDates }) : t("none completed"),
    },
    {
      label: t("Completed on time"),
      value: stats.onTimePct == null ? "—" : `${Math.round(stats.onTimePct)}%`,
      note: stats.completedWithDue
        ? t("of {n} with a due date", { n: stats.completedWithDue })
        : t("no completed order had a due date"),
    },
    {
      label: t("SLA compliance"),
      value: stats.slaCompliancePct == null ? "—" : `${Math.round(stats.slaCompliancePct)}%`,
      note:
        stats.slaMet + stats.slaMissed
          ? t("{met} met of {n} completed with an SLA", { met: stats.slaMet, n: stats.slaMet + stats.slaMissed })
          : t("{n} of {total} have an SLA; none completed yet", { n: stats.slaTracked, total: stats.total }),
      tone: stats.slaCompliancePct != null && stats.slaCompliancePct < 90 ? "bad" : undefined,
    },
    {
      label: t("SLA breached"),
      value: String(stats.slaBreached),
      note: stats.slaAtRisk
        ? t("open past the window · {n} more at risk", { n: stats.slaAtRisk })
        : t("open past the window"),
      tone: stats.slaBreached ? "bad" : undefined,
    },
    {
      label: t("Actual cost"),
      value: stats.withCost ? money(stats.costActual) : "—",
      note: t("{n} of {total} record a cost", { n: stats.withCost, total: stats.total }),
    },
    {
      label: t("Labour hours"),
      value: stats.withLabour ? one(stats.labourHours) : "—",
      note: t("{n} of {total} record hours", { n: stats.withLabour, total: stats.total }),
    },
  ];

  const groups: { title: string; dimension: Dimension; rows: Tally[] }[] = [
    { title: t("By area"), dimension: "area", rows: stats.byArea },
    { title: t("By trade"), dimension: "trade", rows: stats.byTrade },
    { title: t("By contractor"), dimension: "contractor", rows: stats.byContractor },
    { title: t("By repair type"), dimension: "repair", rows: stats.byRepairType },
    { title: t("By discipline"), dimension: "discipline", rows: stats.byDiscipline },
    { title: t("By SLA status"), dimension: "sla", rows: stats.bySla },
    { title: t("By status"), dimension: "status", rows: stats.byStatus },
    { title: t("By priority"), dimension: "priority", rows: stats.byPriority },
    { title: t("By kind"), dimension: "kind", rows: stats.byKind },
  ];

  return (
    <div className={s.stats} data-testid="wo-stats">
      <div className={s.statTiles}>
        {tiles.map((tile) => (
          <div key={tile.label} className={s.statTile}>
            <div className={s.statLabel}>{tile.label}</div>
            <div className={tile.tone === "bad" ? `${s.statValue} ${s.statBad}` : s.statValue}>{tile.value}</div>
            {tile.note && <div className={s.statNote}>{tile.note}</div>}
          </div>
        ))}
      </div>
      <div className={s.breakdowns}>
        {groups.map((g) => (
          <Breakdown
            key={g.dimension}
            title={g.title}
            rows={g.rows}
            total={stats.total}
            active={active?.[g.dimension] ?? null}
            onPick={interactive && onPick ? (key) => onPick(g.dimension, key) : undefined}
          />
        ))}
        <SlaByPriority stats={stats} />
      </div>
    </div>
  );
}

/** Where a contract's service levels are usually set: one row per priority. */
function SlaByPriority({ stats }: { stats: Stats }) {
  return (
    <section className={s.breakdown} aria-label={t("SLA by priority")}>
      <h3 className={s.breakdownTitle}>{t("SLA by priority")}</h3>
      {stats.slaTracked === 0 ? (
        <div className={s.statNote}>
          {t("No order here has an SLA. Set one when raising an order, or on the order itself.")}
        </div>
      ) : (
        <table className={s.slaTable}>
          <thead>
            <tr>
              <th>{t("Priority")}</th>
              <th title={t("Orders with an SLA")}>{t("With SLA")}</th>
              <th title={t("Completed inside the window")}>{t("Met")}</th>
              <th title={t("Completed after the window")}>{t("Missed")}</th>
              <th title={t("Still open, past the window")}>{t("Breached")}</th>
              <th title={t("Met over completed")}>{t("Compliance")}</th>
            </tr>
          </thead>
          <tbody>
            {stats.slaByPriority.map((r) => (
              <tr key={r.priority}>
                <td>P{r.priority}</td>
                <td>{r.tracked}</td>
                <td>{r.met}</td>
                <td className={r.missed ? s.statBad : undefined}>{r.missed}</td>
                <td className={r.breached ? s.statBad : undefined}>{r.breached}</td>
                <td>{r.compliancePct == null ? "—" : `${Math.round(r.compliancePct)}%`}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function Breakdown({
  title,
  rows,
  total,
  active,
  onPick,
}: {
  title: string;
  rows: Tally[];
  total: number;
  active: string | null;
  onPick?: (key: string) => void;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <section className={s.breakdown} aria-label={title}>
      <h3 className={s.breakdownTitle}>{title}</h3>
      {rows.length === 0 ? (
        <div className={s.statNote}>{t("No orders.")}</div>
      ) : (
        <ul className={s.bars}>
          {rows.map((r) => {
            const pct = total ? Math.round((100 * r.count) / total) : 0;
            const tip = t("{label}: {n} of {total} ({pct}%)", { label: r.label, n: r.count, total, pct });
            const body = (
              <>
                <span className={s.barLabel}>{r.label}</span>
                <span className={s.barTrack} aria-hidden="true">
                  {r.count > 0 && <span className={s.barFill} style={{ width: `${(100 * r.count) / max}%` }} />}
                </span>
                <span className={s.barCount}>{r.count}</span>
              </>
            );
            const applied = active != null && active === r.key;
            return (
              <li key={r.key}>
                {onPick ? (
                  <button
                    type="button"
                    className={applied ? `${s.bar} ${s.barApplied}` : s.bar}
                    title={applied ? t("{tip} — click to clear", { tip }) : t("{tip} — click to filter", { tip })}
                    aria-pressed={applied}
                    onClick={() => onPick(r.key)}
                  >
                    {body}
                  </button>
                ) : (
                  <div className={s.bar} title={tip}>
                    {body}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
